import { useEffect, useRef } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from "react-router";
import { useAuth, useHandoff } from "@ncp/api-client";
import { BackofficeLayout, RootLayout } from "./layouts";
import { LoginPage } from "../pages/LoginPage";
import { CasesPage } from "../pages/CasesPage";
import { EconomicConcentrationPage } from "../pages/EconomicConcentrationPage";
import { OtherRequestsRedirect, ProcessRegisterPage } from "../pages/OtherRequestsPage";
import { StatisticsPage } from "../features/statistics/StatisticsPage";
import { CommandCenterPage } from "../pages/CommandCenterPage";
import { InvestigationsPage } from "../pages/InvestigationsPage";
import { LegalKnowledgePage } from "../pages/LegalKnowledgePage";
import { DecisionsPage } from "../pages/DecisionsPage";
import { DataBankPage } from "../pages/DataBankPage";
import { NewsPage } from "../features/news/NewsPage";
import { AdvocacyPage } from "../pages/AdvocacyPage";
import { AdvocacyReviewPage } from "../pages/AdvocacyReviewPage";
import { CaseWorkspacePage } from "../features/case/CaseWorkspacePage";
import { AgenticPage } from "../features/agentic/AgenticPage";
import { ScreenPage } from "../pages/ScreenPage";
import { adminHref } from "../lib/links";
import { RequestsRegisterPage } from "../features/requests/RequestsRegisterPage";

/** The platform administration screens moved to the Platform Console; old bookmarks are forwarded there. */
function ConsoleRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  const { navigate } = useHandoff();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void navigate(adminHref(`${to}${search}${hash}`), { replace: true });
  }, [navigate, to, search, hash]);
  return null;
}

/** The AI Inbox is part of the Command Center; its deep links (?agent=…, ?proposal=…) keep working. */
function InboxRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("view", "inbox");
  return <Navigate to={`/?${params.toString()}`} replace />;
}

function RequireStaff() {
  const { isAuthenticated, user } = useAuth();
  const { pathname, search, hash } = useLocation();
  if (!isAuthenticated || user?.kind !== "Staff") return <Navigate to="/login" replace state={{ from: `${pathname}${search}${hash}` }} />;
  return <Outlet />;
}

/** The case page, mounted afresh for each file (scenario 6.16): nothing unsaved on one file's decision or workbenches carries into the next. */
function CaseWorkspaceRoute() {
  const { id } = useParams();
  return <CaseWorkspacePage key={id} />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      {
        element: <RequireStaff />,
        children: [
          // Full screen, outside the staff shell: the agentic review of a concentration file (a new notification, or a filed one).
          { path: "concentration/agentic", element: <AgenticPage /> },
          { path: "concentration/agentic/:caseId", element: <AgenticPage /> },
          {
            element: <BackofficeLayout />,
            children: [
              { index: true, element: <CommandCenterPage /> },
              { path: "inbox", element: <InboxRedirect /> },
              { path: "statistics", element: <StatisticsPage /> },
              { path: "concentration", element: <EconomicConcentrationPage /> },
              { path: "requests", element: <OtherRequestsRedirect /> },
              { path: "exemptions", element: <ProcessRegisterPage type="Exemption" /> },
              { path: "grievances", element: <ProcessRegisterPage type="Appeal" /> },
              { path: "legal-enquiries", element: <ProcessRegisterPage type="LegalEnquiry" /> },
              { path: "cases", element: <CasesPage /> },
              { path: "cases/:id", element: <CaseWorkspaceRoute /> },
              { path: "decisions", element: <DecisionsPage /> },
              { path: "investigations", element: <InvestigationsPage /> },
              { path: "data-bank", element: <DataBankPage /> },
              { path: "outside-requests", element: <RequestsRegisterPage /> },
              { path: "news", element: <NewsPage /> },
              { path: "legal-knowledge", element: <LegalKnowledgePage /> },
              { path: "advocacy", element: <AdvocacyPage /> },
              { path: "advocacy/:id", element: <AdvocacyReviewPage /> },
              { path: "s/:screenKey", element: <ScreenPage /> },
              { path: "workflow-studio", element: <ConsoleRedirect to="/workflows" /> },
              { path: "integrations", element: <ConsoleRedirect to="/integrations" /> },
              { path: "ai-governance", element: <ConsoleRedirect to="/governance" /> },
              { path: "audit", element: <ConsoleRedirect to="/audit" /> },
              { path: "*", element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
], { basename: import.meta.env.BASE_URL });
